import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { find, head } from 'lodash-es';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of, throwError, timer } from 'rxjs';
import { catchError, concatMap, delayWhen, exhaustMap, map, switchMap, tap } from 'rxjs/operators';
import { AuthService, BusinessAccountService, StorageService, EmployeeService } from '@shared/services';
import { AUTH_ERROR_CODE, constants, MESSAGE } from '@shared/constants';
import { AuthActions } from './auth.actions';
import { authFeature, ModalsActions, selectQueryParams, ToastActions } from '..';

export const authLogin$ = createEffect(
  (actions$ = inject(Actions), authService = inject(AuthService), employeeService = inject(EmployeeService)) => {
    return actions$.pipe(
      ofType(AuthActions.authLogin),
      exhaustMap(({ email, password }) => {
        return authService
          .getAuthTenantInfo()
          .pipe(catchError(() => throwError(() => new Error(MESSAGE.AUTH_PROVIDER_VERIFICATION_ERROR))))
          .pipe(
            exhaustMap(({ providerTenantId }) => {
              return authService
                .googleCloudLogin(email, password, providerTenantId)
                .pipe(catchError((error) => throwError(() => new Error(error.message))));
            }),
            exhaustMap(({ token, userId }) =>
              authService.exchangeToken(token).pipe(
                map(() => ({ userId })),
                catchError((error) => {
                  // Login blocked by an expired password: carry the fresh provider token so the user
                  // can set a new one without signing in again. The interceptor passes the body through.
                  if (error?.errorCode === AUTH_ERROR_CODE.passwordExpired) {
                    return throwError(() => ({ passwordExpired: true, providerToken: token, email }));
                  }
                  return throwError(() => new Error(MESSAGE.UNKNOWN_ERROR));
                }),
              ),
            ),
            exhaustMap(({ userId }) =>
              employeeService.getEmployeeDetails(userId).pipe(catchError(() => throwError(() => new Error(MESSAGE.UNKNOWN_ERROR)))),
            ),
            switchMap((user) => {
              return authService.getLinkedBusinessAccounts().pipe(
                map((linkedBusinessAccounts) => {
                  const businessAccount = find(linkedBusinessAccounts, ['parent', true]) || head(linkedBusinessAccounts);

                  return { user, businessAccount: businessAccount ?? null };
                }),
                catchError(() => {
                  return of({ user: null, businessAccount: null });
                }),
              );
            }),
            map(({ user, businessAccount }) => {
              return AuthActions.authLoginSuccess({ user, businessAccount });
            }),
            catchError((error) => {
              if (error?.passwordExpired) {
                return of(AuthActions.passwordExpired({ providerToken: error.providerToken, email: error.email }));
              }
              return of(AuthActions.authLoginFailure({ error: error.message }));
            }),
          );
      }),
    );
  },
  { functional: true },
);

export const passwordExpiredRedirect$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) => {
    return actions$.pipe(
      ofType(AuthActions.passwordExpired),
      tap(({ providerToken, email }) => {
        // Pass the fresh provider token via in-memory router state only — never persisted.
        router.navigate(['auth/set-new-password'], { state: { providerToken, email } });
      }),
    );
  },
  { functional: true, dispatch: false },
);

export const loginRedirect$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), router = inject(Router)) => {
    return actions$.pipe(
      ofType(AuthActions.authLoginSuccess, AuthActions.loginRedirect),
      concatLatestFrom(() => store.select(selectQueryParams)),
      tap({ next: ([, queryParams]) => router.navigateByUrl(queryParams?.returnUrl || constants.HOME_ROUTE) }),
    );
  },
  { functional: true, dispatch: false },
);

export const authLogout$ = createEffect(
  (actions$ = inject(Actions), authService = inject(AuthService)) => {
    return actions$.pipe(
      ofType(AuthActions.authLogout),
      exhaustMap(({ params }) => {
        return authService.logout().pipe(map(() => AuthActions.authLogoutCompleted({ params })));
      }),
    );
  },
  { functional: true },
);

export const logoutCompleted$ = createEffect(
  (actions$ = inject(Actions), storageService = inject(StorageService), router = inject(Router)) => {
    return actions$.pipe(
      ofType(AuthActions.authLogoutCompleted),
      tap({
        next: ({ params }) => {
          const { skipNavigation, isReturnUrl } = params || {};
          const returnUrl = router.url;

          if (!skipNavigation) {
            const routerQueryParams =
              isReturnUrl && ![constants.HOME_ROUTE, '404', '/'].includes(returnUrl) ? { queryParams: { returnUrl } } : undefined;
            router.navigateByUrl(constants.AUTH_ROUTE, { replaceUrl: true, ...routerQueryParams });
          }

          storageService.removeItem(constants.KOR_BUSINESS_ACCOUNT_FILTER);
          storageService.removeItem(constants.KOR_BUSINESS_ACCOUNT_DOCUMENT_FILTER);
        },
      }),
      map(() => ModalsActions.dismissAll()),
    );
  },
  { functional: true },
);

export const refreshToken$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), authService = inject(AuthService)) => {
    return actions$.pipe(
      ofType(AuthActions.refreshToken, AuthActions.authLoginSuccess, AuthActions.rehydrateSession),
      concatLatestFrom(() => [store.select(authFeature.selectIsLoggedIn), store.select(authFeature.selectBusinessAccount)]),
      concatMap(([, isLoggedIn, businessAccount]) => {
        if (isLoggedIn && businessAccount) {
          return authService.refreshToken(businessAccount.id).pipe(
            map((tokenInfo) => {
              return AuthActions.refreshTokenSuccess({ tokenInfo });
            }),
            catchError(() => of(AuthActions.authLogout({ params: { isReturnUrl: true } }))),
          );
        } else {
          return of(AuthActions.noActiveSession());
        }
      }),
    );
  },
  { functional: true },
);

export const refreshTokenSuccess$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(AuthActions.refreshTokenSuccess),
      concatLatestFrom(() => store.select(authFeature.selectTokenExpireInMilliseconds)),
      delayWhen(([, expiresIn]) => timer(expiresIn * 0.8)),
      map(() => AuthActions.refreshToken()),
    );
  },
  { functional: true },
);

export const loadIndustries$ = createEffect(
  (actions$ = inject(Actions), businessAccountsService = inject(BusinessAccountService)) => {
    return actions$.pipe(
      ofType(AuthActions.authLoginSuccess),
      exhaustMap(() => {
        return businessAccountsService.getIndustries({ size: 200 }).pipe(
          map((industries) => AuthActions.loadIndustriesSuccess({ industries })),
          catchError((error) => of(AuthActions.loadIndustriesFailure({ error }))),
        );
      }),
    );
  },
  { functional: true },
);

export const loadIndustriesFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(AuthActions.loadIndustriesFailure),
      map(() => ToastActions.error({ message: 'Unable to fetch Industry Codes.' })),
    );
  },
  { functional: true },
);
